import { useEffect, useMemo, useRef, useState } from 'react';
import {
    AlertTriangle, CalendarDays, Check, Loader2, Search, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { BULK_ACTIONS, statusLabel } from '@/lib/appointmentStatuses';
/* ⭐ `staleness` MOVED OUT OF THIS FILE 2026-09-27 when the order detail needed the
   same sentence — see its note in `lib/utils.ts`. Behaviour byte-identical; only the
   address changed, so this row's `synced 9d ago` reads exactly as before. */
import { staleness } from '@/lib/utils';
/* ⭐ THE BOARD'S ROW, REUSED (2026-09-20). ⛔ `APPOINTMENT_STATUSES` is no longer
   imported here: this file used to iterate it to build a lookalike strip, and the
   shared component owns that loop now. Leaving the import would have been a silent
   invitation to grow a second copy beside the first. */
import { StatusFilterBar } from '@/components/StatusFilterBar';
import {
    searchAppointments,
    bulkAppointmentStatus,
    type AppointmentSearchHit,
    type AppointmentSearchResult,
    type SearchScope,
    type WmsSearchHit,
} from '@/api/client';

/**
 * APPOINTMENT SEARCH ON THE MAIN DOCK SCHEDULE SCREEN (#145, #254, #255).
 *
 * Pete, 2026-08-24: *"We need to be able to search appointments by most of the info
 * and it needs to be place on the main dock schedule screen. This is so the scheduler
 * doesn't have to scroll thru screens to find current, old or future appointments."*
 *
 * ⭐ LIST FIRST, THEN CLICK TO JUMP — Pete chose this over either alone. The list
 * answers "show me everything for this client"; the click answers "take me to this
 * truck". One without the other is half the feature.
 *
 * ⛔⛔ THE FAILURE THIS WHOLE COMPONENT IS SHAPED AROUND: a search that quietly
 * returns less than it should. Nothing throws, nothing logs, and the scheduler
 * concludes the load is not booked — then a driver turns up at a gate for it. So this
 * screen always says what was searched, always says when the list was cut, and never
 * renders an empty box with no explanation.
 *
 * ---- ⭐ #255 — THE SCOPE FOLLOWS THE CALENDAR ------------------------------
 *
 * Pete, 2026-08-26: *"lets correspond the search date with the select Day, Week,
 * Month next to it instead of the 90 + days thing. You can still select Search All
 * dates after that."*
 *
 * ⛔ AND THAT MAKES "NOTHING FOUND" FAR MORE COMMON, WHICH IS THE RISK. A one-day
 * default is easier to read and much easier to be misled by. Two things pay for it:
 * the scope sentence, which always states what actually ran, and `outsideScope` —
 * the server counting matches on OTHER dates so an empty list can offer the next
 * rung instead of being a dead end.
 *
 * ---- ⛔⛔ #254 — THE BULK BAR, AND WHY IT IS BUILT SO CAREFULLY -------------
 *
 * Everything else on this screen reads. This writes, to an unbounded set, from a
 * list somebody may not have read to the bottom of. Four rules:
 *
 * ⛔ SELECT-ALL MEANS THE ROWS ON SCREEN. Never "everything the filter matched".
 * A select-all that silently included rows beyond the 50-row cap is how a day's
 * schedule gets cancelled by one click, and the row that was cut is invisible by
 * definition.
 *
 * ⛔ THE CONFIRM NAMES THE COUNT AND WHAT WILL HAPPEN, and it is ConfirmDialog —
 * `window.confirm` is banned here ("this is lazy coding and we never do this").
 *
 * ⛔ CANCEL IS NOT ADJACENT TO APPROVE and is flagged destructive. A mis-click
 * between neighbouring dropdown items must not be the difference between confirming
 * a day's bookings and cancelling them.
 *
 * ⛔ AND THE RESULT IS REPORTED PER ROW. "37 changed, 3 skipped" with the reasons —
 * a batch that half-applies and says "done" is worse than one that fails.
 */

interface AppointmentSearchProps {
    facilityCode: string;
    /** The day the board is standing on — the scope is computed from here. */
    anchorDate: string;
    /**
     * ⛔⛤ 2026-09-22 -- ~~viewMode: 'day' | 'week' | 'month';~~ REMOVED.
     *
     * ⭐ #255's reasoning, struck with it: *"THE CALENDAR'S OWN VIEW MODE. The
     * search covers what the scheduler can see, so this is the default scope
     * rather than a separate setting they would have to keep in step by hand."*
     *
     * ⛔ That reasoning died with the default. The scope starts at 'all' now and
     * the view mode drives nothing here -- tsc found it unused the moment the
     * last reader went, which is the only reason it is not still sitting in the
     * signature pretending to matter.
     *   ** A PROP NOTHING READS IS A LIE ABOUT WHAT A COMPONENT DEPENDS ON, AND
     *      THE NEXT PERSON WILL KEEP IT IN STEP FOR NOTHING. **
     * ⚠️ The rungs are unchanged; a scheduler still picks day/week/month INSIDE
     * the search box. What went is the calendar silently choosing for them.
     */
    getToken: () => Promise<string>;
    /** ⭐ Navigate the calendar and hold a highlight on the chosen appointment. */
    /** ⭐ `facilityCode` (decision 210) is passed only for a cross-facility hit. */
    onJump: (localDate: string, appointmentId: string, facilityCode?: string) => void;
    /**
     * ⭐ INCREMENT 4 — scope the whole board to a party from a search result.
     * ⛔ NO APPOINTMENTS ARE PASSED. The search looks ACROSS DATES; the board is
     * standing on ONE. Handing over hits from other days would build a focus whose
     * loads are not on screen, so the PAGE derives the party's appointments from the
     * day it is actually showing.
     *   ** A SCOPE MUST BE BUILT FROM WHAT THE SCREEN IS SHOWING, NOT FROM WHAT
     *      FOUND IT. **
     * ⚠️ A party with nothing booked today therefore scopes to an empty board — which
     * is the truth, and `focusBlockedReason` already words it on the rails.
     */
    /**
     * ⛔⚤ ~~`kind: 'client' | 'carrier'`~~ — WIDENED to include consignee,
     * 2026-09-25. Pete: *"that will actually be the same for carrier, consignee"*.
     * ⚠️ THE HANDLER COULD ALWAYS TAKE ONE. Calendar passes `kind` straight into
     * `chooseSubject` and `chooseParty`, and both take `PanelSubject` /
     * `PartyKind`, which have included 'consignee' all along. The restriction was
     * in THIS TYPE and nowhere else, so consignee scoping was one word away from
     * working and read as unimplemented.
     *   ** A PROP TYPE NARROWER THAN ITS HANDLER IS A FEATURE SWITCHED OFF IN A
     *      PLACE NOBODY THINKS TO LOOK. **
     */
    onScopeParty?: (kind: 'client' | 'carrier' | 'consignee', code: string, name: string) => void;
    /**
     * ⭐ THE PARTIES ON THE BOARD RIGHT NOW — the fallback when the search itself
     * returns nothing. Supplied by `Calendar.tsx` from the appointments already
     * loaded, so this adds no fetch and no backend work, which was the original
     * constraint on the pinned rows.
     */
    boardParties?: { kind: 'client' | 'carrier' | 'consignee'; code: string; name: string }[];
    /** ⚠️ #254 — the board is stale after a bulk change; the caller reloads it. */
    onBulkChanged?: () => void;
}

/**
 * ---- ⬜⬜ BULK STATUS ACTIONS: **SUSPENDED IN SEARCH**, NOT DELETED (2026-09-27) --
 *
 * > Pete: *"I do not like the ability to approve, noshow, cancel an appointment from
 * > search … Search should just be showing results and filter functionality - no other
 * > functionality in there - too confusing"* → then, on the feature itself: *"I guess
 * > that might be nice to mass update appointments but it shouldn't be in the search
 * > bar. Can you suppress that for right now … I may move that functionality
 * > somewhere else"*.
 *
 * ⭐ THE RULE HE STATED IS THE ONE TO KEEP: **SEARCH FINDS THINGS. IT DOES NOT DO
 * THINGS.** A control that acts belongs where the thing it acts on lives — and since
 * this morning a single click on a result scopes the entire screen to that load, so
 * "the normal places" are one click away.
 *
 * ---- ⛔ WHY A FLAG AND NOT A DELETION ------------------------------------------
 *
 * He said "suppress" and "I may move that functionality somewhere else" — so the
 * machinery is intact and ONE LINE from returning, wherever it lands. Deleting it
 * would mean rebuilding `applyBulk`, the skip report and the reason-required confirm
 * from scratch, and those carry decisions that are not obvious a second time (Cancel
 * requires a reason; carriers are deliberately NOT notified; the skipped list is
 * reported per appointment).
 *   ** "REMOVE IT FROM HERE" AND "REMOVE IT" ARE DIFFERENT INSTRUCTIONS, AND THE
 *      CHEAPER ONE TO GET WRONG IS THE FIRST. **
 *
 * ⚠️ A NAMED CONSTANT, NOT A BARE `false &&`. The name is the difference between
 * suppressed-on-purpose and dead code somebody deletes in six weeks while tidying.
 * ⚠️ AND IT IS NOT A SETTING. There is no UI to flip it and there should not be:
 * shipping both behaviours would leave two schedulers looking at different screens.
 *
 * ⬜ Open item — the capability now has no entry point anywhere in the app.
 * `bulkAppointmentStatus` and its endpoint are unreachable until Pete chooses a home.
 */
const BULK_IN_SEARCH = false;

export function AppointmentSearch({
    facilityCode, anchorDate, getToken, onJump, onBulkChanged, onScopeParty,
    boardParties,
}: AppointmentSearchProps) {
    const [q, setQ] = useState('');
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [res, setRes] = useState<AppointmentSearchResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    /**
     * ---- ⭐⭐ THE DATE IS AN OPTION, NOT THE DEFAULT (2026-09-22) -----------
     *
     * ⛔ Pete: *"I think we need to set date as an option, not as the default. We
     * are missing lots of records with the date range set."*
     *
     * ⛔⛤ It was ~~useState<SearchScope>(viewMode)~~ -- the calendar's own view
     * mode. That default was chosen in #255 and it is what hid `A00000364`.
     *   ** A DEFAULT CHOSEN TO PROTECT THE DATABASE IS PAID FOR BY THE PERSON
     *      LOOKING FOR SOMETHING. **
     * ⚠️ The rungs are all still here; the scheduler narrows deliberately now
     * instead of widening deliberately.
     */
    const [scope, setScope] = useState<SearchScope>('all');
    /**
     * ⭐ #262 — THE DATE THE SEARCH IS ANCHORED TO, editable here.
     *
     * Pete, 2026-08-26: *"If we are on Day filter, it searches the current day which
     * is great but would be able to change the date in the search box would be
     * nice."*
     *
     * ⛔ IT IS A LOCAL COPY OF `anchorDate`, NOT A SECOND SOURCE OF TRUTH. The
     * calendar still owns which day the board is standing on; this is the scheduler
     * asking a question about a DIFFERENT day without leaving the box and without
     * moving the board out from under themselves. ⚠️ Changing it does not navigate
     * the calendar — jumping to a result still does, which is the deliberate act.
     *
     * ⚠️ AND IT FOLLOWS THE BOARD, exactly like `scope` follows `viewMode`. A
     * scheduler who moves the calendar to next Tuesday and then searches means
     * Tuesday; a stale date left over from an earlier question would answer about
     * a day nothing on screen is showing.
     */
    const [searchDate, setSearchDate] = useState<string>(anchorDate);
    const [statuses, setStatuses] = useState<string[]>([]);
    const [picked, setPicked] = useState<Set<string>>(new Set());
    const [pendingAction, setPendingAction] = useState<string | null>(null);
    const [bulkBusy, setBulkBusy] = useState(false);
    /**
     * ---- ⭐⭐⭐ THE AUTOMATIC WIDEN, AND THE WAY BACK OUT (Pete, 2026-09-20) -----
     *
     * > *"Widen to this week — run the search automatically, with a Cancel."*
     *
     * ⛔⛔ NOT NULL MEANS "THE SCREEN CHANGED THE QUESTION AND THE SCHEDULER DID
     * NOT." That is the entire reason this state exists, and it is why Cancel is
     * not optional garnish: a search that silently answers a WIDER question than
     * the one typed is how a scheduler reads next Thursday's load as today's.
     *   ** A SCREEN MAY BROADEN A QUESTION ON ITS OWN ONLY IF IT SAYS SO AND
     *      OFFERS THE NARROW ANSWER BACK. **
     *
     * ⭐ `from` IS THE RUNG WE LEFT, so Cancel restores a measured value rather
     * than recomputing "one narrower", which is the mirror of the reason the
     * manual button takes `nextScope` from the server instead of deriving it.
     * ⭐ `toLabel` IS THE SERVER'S OWN WORDING (`nextScopeLabel`), so the banner
     * and the button that would have done this by hand cannot drift apart.
     */
    const [autoWiden, setAutoWiden] = useState<{ from: SearchScope; toLabel: string } | null>(null);
    const [bulkReport, setBulkReport] = useState<{ changed: number; skipped: { appointmentId: number; reason: string }[] } | null>(null);
    const boxRef = useRef<HTMLDivElement>(null);

    /**
     * ⚠️ THE SCOPE FOLLOWS THE VIEW UNTIL THE SCHEDULER OVERRIDES IT, and then it
     * follows the view again on the next switch.
     *
     * ⛔ NOT "the view sets it once and never again" — a scheduler who widens to all
     * dates, then clicks Week on the calendar, has clearly changed what they are
     * looking at. And not "the view always wins" either, or the widening buttons
     * would silently snap back. Resetting on a VIEW CHANGE is the rule that matches
     * both intentions.
     */
    /* ⛔⛤ 2026-09-22 -- ~~useEffect(() => { setScope(viewMode); }, [viewMode]);~~
       THE VIEW MODE NO LONGER DRIVES THE SEARCH SCOPE. With 'all' as the default
       this effect would undo it every time somebody clicked Day, Week or Month --
       the default would last exactly until the next view change and nobody would
       understand why.
         ** A DEFAULT THAT SOMETHING ELSE KEEPS OVERWRITING IS NOT A DEFAULT. **
       ⚠️ The reasoning struck with it was sound for the OLD default: a scheduler
       who widened and then clicked Week had changed what they were looking at.
       Narrowing is now the deliberate act, and a deliberate act must not be
       undone by a view change either -- the rule survives, its direction flipped.
       ⭐ `searchDate` still follows the board; the date is an input to a narrowed
       search, not the scope itself. */
    // ⚠️ Same rule as searchDate below, in the other direction - see above.
    // ⚠️ Same rule, same reason — see `searchDate`.
    useEffect(() => { setSearchDate(anchorDate); }, [anchorDate]);

    /**
     * ⛔ SEARCH IS EXPLICIT — Enter or the button. NOT debounced-as-you-type.
     *
     * ⚠️ THIS IS THE SAME CHOICE PoFinder MADE, and for a better reason here: the
     * query runs LIKE across thirteen columns plus two EXISTS subqueries, and a
     * keystroke-triggered version fires that ~6 times for "PWN590". ⭐ It also removes
     * a whole class of bug where a slow response for "PWN" lands after a fast one for
     * "PWN5900139" and silently replaces the right answer with the wrong one.
     */
    /**
     * ⭐⭐ `allowAutoWiden` IS THE RE-ENTRY BRAKE, AND IT IS A PARAMETER FOR THE SAME
     * REASON `useScope` IS: a flag in state would be read one render too late, and
     * this function calls ITSELF. ⛔ It is `false` on the widened call, so the search
     * can step out exactly ONE rung on its own — the same "one rung at a time" rule
     * the manual button follows, applied to the automatic path.
     * ⛔⛔ AND IT IS `false` AT THE MANUAL WIDEN SITES TOO. A scheduler who clicked
     * *"All dates"* has chosen a rung; widening past it because that rung was also
     * empty would overrule a deliberate act with a guess.
     *   ** AUTOMATION MAY FILL A SILENCE. IT MAY NOT OVERRIDE AN ANSWER. **
     */
    /**
     * ---- ⭐⭐ CANCEL, AND WHY IT IS A RUN ID RATHER THAN AN ABORT ------------
     *
     * ⛔ Pete: *"Screenshot 3 seems to continue to search even after the results
     * pull up. There should be a cancel search button next to the search button."*
     *
     * ⚠️ THE SPINNER HE SAW WAS PROBABLY REAL AND PROBABLY THE AUTO-WIDEN: an empty
     * scoped result fires a SECOND search while the first one's (empty) list is
     * already on screen. With all-dates as the default that path rarely runs now,
     * but "rarely" is not "never" and he asked for a button.
     *
     * ⛔⛔ IT DOES NOT ABORT THE REQUEST, AND THE NAME MUST NOT IMPLY IT DOES. The
     * HTTP call is already in flight and `searchAppointments` takes no signal;
     * what this stops is the SCREEN WAITING, and it discards the answer when it
     * lands so a cancelled search can never repaint the list underneath a new one.
     *   ** A CANCEL THAT SILENTLY LETS THE OLD ANSWER WIN IS WORSE THAN NO CANCEL:
     *      IT LOOKS LIKE THE SEARCH RAN TWICE. **
     * ⭐ A monotonic id is also the fix for a plain race -- two searches in flight,
     * the slower one landing last -- which no abort would have solved either.
     */
    const runIdRef = useRef(0);
    function cancelSearch() {
        runIdRef.current += 1;
        setBusy(false);
    }

    async function run(
        useScope: SearchScope = scope,
        useStatuses: string[] = statuses,
        useDate: string = searchDate,
        allowAutoWiden = true,
    ) {
        const myRun = runIdRef.current + 1;
        runIdRef.current = myRun;
        setBusy(true);
        setError(null);
        setBulkReport(null);
        /* ⚠️ CLEARED ON EVERY FRESH SEARCH, not on every call — the widened call
           passes `false` and must not wipe the banner the call above just set. */
        if (allowAutoWiden) setAutoWiden(null);
        try {
            const token = await getToken();
            /**
             * ⛔ `useDate`, NOT `anchorDate` (#262). The scheduler may have moved the
             * search's own date without moving the board. ⚠️ Passed as an ARGUMENT
             * rather than read from state, for the same reason `useScope` and
             * `useStatuses` are: a handler that sets state and then calls run() would
             * search with the PREVIOUS value, because setState is not synchronous.
             * That bug has already been avoided twice in this function; it would be
             * invisible in testing and permanent in use.
             */
            const r = await searchAppointments(token, facilityCode, q, useDate, useScope, useStatuses);
            /* ⛔ THE GUARD IS BEFORE THE FIRST setState, not after. A cancelled run
               that still calls setRes has not been cancelled, it has been delayed. */
            if (runIdRef.current !== myRun) return;
            setRes(r);
            /**
             * ⛔ THE SELECTION IS DROPPED ON EVERY SEARCH. Rows that are no longer on
             * screen must not stay selected — a scheduler who searches again and hits
             * Approve would otherwise act on a set they can no longer see, which is
             * precisely the failure the select-all rule exists to prevent.
             */
            setPicked(new Set());
            setOpen(true);
            /**
             * ---- ⭐⭐⭐ NOTHING HERE, AND SOMEWHERE TO LOOK: GO LOOK ---------------
             *
             * ⛔ THE CONDITION IS `results.length === 0`, NOT "few results". A widen
             * that fires over a short-but-real list would replace an answer with a
             * bigger answer — and the scheduler would never learn that the load they
             * typed IS on the day they are standing on.
             *
             * ⛔⛔ AND IT DOES NOT FIRE ON A REFUSAL OR AN ERROR. `refused` is a
             * sentence the server chose to show ("type at least two characters");
             * searching a wider range for the same refusal produces the same refusal
             * over more rows of database.
             *
             * ⚠️ `r.nextScope` IS THE SERVER'S PERMISSION, not a computed guess. When
             * it is null there is no rung left and the empty result stands, which is
             * the honest end of the sentence: nothing matches anywhere we can look.
             */
            if (allowAutoWiden && !r.refused && r.results.length === 0 && r.nextScope) {
                setAutoWiden({
                    from: useScope,
                    /* ⚠️ The server sends the label with the rung; the fallback covers
                       a `nextScope` that arrives without one, which would otherwise
                       render the word "null" inside a sentence. */
                    toLabel: r.nextScopeLabel ?? 'a wider range',
                });
                setScope(r.nextScope);
                await run(r.nextScope, useStatuses, useDate, false);
            }
        } catch (err) {
            if (runIdRef.current !== myRun) return;
            setError(err instanceof Error ? err.message : String(err));
            setOpen(true);
        } finally {
            /* ⚠️ ONLY THE CURRENT RUN MAY CLEAR THE SPINNER. A superseded run
               finishing late would otherwise stop the spinner for the search that
               is still going. */
            if (runIdRef.current === myRun) setBusy(false);
        }
    }

    /** ⚠️ Click-away closes the panel, but never clears the query — retyping a
     *  container number because the panel closed is exactly the friction this
     *  feature exists to remove. */
    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            // ⚠️ NOT WHILE A CONFIRM IS UP. The dialog is portalled outside this box,
            // so a click on its Confirm button reads as a click-away and would close
            // the panel underneath the very action it is confirming.
            if (pendingAction) return;
            if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open, pendingAction]);

    function clear() {
        setQ('');
        setRes(null);
        setError(null);
        setStatuses([]);
        /* ⛔ CLEAR RETURNS TO THE DEFAULT, which is now all dates -- not to the
           view mode. An X that restored a narrowing the scheduler had just
           cleared would be the one control that does the opposite of its name. */
        setScope('all');
        setSearchDate(anchorDate);      // ⚠️ Back to the board's day, not a stale one.
        setPicked(new Set());
        setBulkReport(null);
        /* ⚠️ The widen belongs to ONE question. Carrying the banner past the X would
           explain a broadening that no longer applies to anything on screen. */
        setAutoWiden(null);
        setOpen(false);
    }

    const hits = res?.results ?? [];
    /**
     * ⭐ HOW MANY ORDERS THE **WMS** HALF FOUND — read by the empty state so it can
     * stop saying "nothing" while a match is on screen (Pete, 2026-09-27).
     * ⚠️ `?? 0` COVERS TWO DIFFERENT ABSENCES: no search has run, and the WMS could
     * not be reached. Both mean "no orders to point at", and the unavailable case is
     * already answered by its own amber banner rather than by this number.
     */
    const wmsFound = res?.wms?.results.length ?? 0;
    /**
     * ---- ⭐⭐ DECISION 210 — THE EXACT HITS, KEPT APART ---------------------
     *
     * ⛔ NOT CONCATENATED INTO `hits`, and that is not a style choice. `Select All`,
     * `picked`, the bulk-status actions and the `capped` count are all computed over
     * `hits`; folding a cross-facility row in would put a load from ANOTHER SITE
     * inside a selection the scheduler believes is the list in front of them, and
     * one click of Approve would act on it.
     *   ** A ROW THAT CANNOT BE ACTED ON WITH THE OTHERS MUST NOT SIT IN THE SAME
     *      LIST AS THE OTHERS. **
     * ⚠️ Empty is the normal case: the server only runs the exact pass when the
     * typed text looks like a whole identifier.
     */
    const exactHits = res?.exactHits ?? [];

    /**
     * ---- ⭐⭐ INCREMENT 4: THE SEARCH CAN SELECT A WHOLE PARTY (decision 188 ⓗ) ----
     *
     * ⭐ Spec: a pinned *"All Traffix appointments"* row above the results — you typed
     * a carrier's name, so offer the carrier, not just the loads that happened to
     * match.
     *
     * ⛔⛔ DERIVED FROM THE RESULTS, SO THERE IS NO BACKEND WORK AND NO SECOND FETCH.
     * `AppointmentSearchHit` already carries `clientCode/clientName` and
     * `carrierCode/carrierName`; the parties are simply the distinct ones the server
     * has already told us about. ⚠️ The spec said *"keeping the search untouched"* —
     * nothing about the query, the scope or `run()` changes.
     *
     * ⛔ ONLY PARTIES WHOSE NAME OR CODE MATCHES WHAT WAS TYPED. Every hit has a
     * client and a carrier, so pinning all of them would put a row above the results
     * for parties the scheduler never mentioned — and the top of a result list is the
     * most expensive real estate on the screen.
     *   ** A SUGGESTION NOBODY ASKED FOR IS INDISTINGUISHABLE FROM A WRONG RESULT. **
     *
     * ⚠️ CAPPED AT THREE. A two-letter query can match a dozen parties, and a wall of
     * pinned rows would bury the results it is supposed to introduce.
     */
    /**
     * ---- ⛔⛔⚤ ~~`|| hits.length === 0`~~ — REMOVED 2026-09-25 ------------------
     *
     * > Pete wanted typing a client name to scope the board to that client.
     *
     * ⛔ THE OFFER ONLY EVER APPEARED WHEN THE SEARCH HAD ALREADY SUCCEEDED. Type a
     * client whose loads are on another date, or who has none this week, and the
     * search returns nothing - so there were no hits to derive a party from, and the
     * one row that would have helped was the row that could not be built.
     *   ** A SUGGESTION DERIVED FROM THE RESULTS CANNOT RESCUE AN EMPTY RESULT. **
     *
     * ⭐ TWO SOURCES NOW, IN PRIORITY ORDER. Hits still rank first and still carry a
     * count, because a party with six matching loads is a better offer than one with
     * none. `boardParties` fills the gaps: the consignee arm that never existed, and
     * every party whose name matches while the search found nothing.
     *
     * ⚠️ THE ORIGINAL CONSTRAINT SURVIVES. That note insisted on "no backend work
     * and no second fetch" - `boardParties` is the board's own appointments, already
     * in memory.
     *
     * ⚠️ A BOARD PARTY HAS NO COUNT, AND IS NOT GIVEN A FAKE ONE. `n: 0` sorts it
     * below every hit-derived row, which is the correct ordering: an offer that
     * matched the query is worth more than one that merely exists today.
     */
    const pinnedParties = useMemo(() => {
        const needle = q.trim().toLowerCase();
        if (needle.length < 2) return [];
        const matches = (code: string, label: string) =>
            label.toLowerCase().includes(needle) || code.toLowerCase().includes(needle);
        const seen = new Map<string, { kind: 'client' | 'carrier' | 'consignee'; code: string; name: string; n: number }>();
        for (const h of hits) {
            for (const [kind, code, name] of [
                ['client', h.clientCode, h.clientName] as const,
                ['carrier', h.carrierCode, h.carrierName] as const,
            ]) {
                if (!code) continue;
                const label = name || code;
                if (!matches(code, label)) continue;
                const key = `${kind}:${code}`;
                const prev = seen.get(key);
                if (prev) prev.n += 1;
                else seen.set(key, { kind, code, name: label, n: 1 });
            }
        }
        for (const p of boardParties ?? []) {
            if (!p.code || !matches(p.code, p.name)) continue;
            const key = `${p.kind}:${p.code}`;
            if (!seen.has(key)) seen.set(key, { kind: p.kind, code: p.code, name: p.name, n: 0 });
        }
        return [...seen.values()].sort((a, b) => b.n - a.n).slice(0, 3);
    }, [hits, q, boardParties]);

    const toggleStatus = (code: string) => {
        const next = statuses.includes(code) ? statuses.filter(c => c !== code) : [...statuses, code];
        setStatuses(next);
        // ⚠️ RE-RUN IMMEDIATELY. A filter that needs a second click on the search
        // button to take effect shows a list that disagrees with the ticked boxes.
        if (res) void run(scope, next);
    };

    const togglePick = (id: string) => {
        setBulkReport(null);
        setPicked(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const allOnScreenPicked = hits.length > 0 && hits.every(h => picked.has(h.appointmentId));
    const toggleAll = () => {
        setBulkReport(null);
        setPicked(allOnScreenPicked ? new Set() : new Set(hits.map(h => h.appointmentId)));
    };

    const action = useMemo(
        () => BULK_ACTIONS.find(a => a.action === pendingAction) ?? null,
        [pendingAction],
    );

    async function applyBulk(reason?: string) {
        if (!action) return;
        setBulkBusy(true);
        setError(null);
        try {
            const token = await getToken();
            const out = await bulkAppointmentStatus(token, {
                facilityCode,
                // ⛔ THE ROWS ON SCREEN, as ids. Not a filter the server re-runs.
                appointmentIds: Array.from(picked).map(Number).filter(Number.isSafeInteger),
                action: action.action,
                reason: reason ?? null,
            });
            setBulkReport({ changed: out.changed, skipped: out.skipped });
            setPicked(new Set());
            onBulkChanged?.();
            // ⭐ RE-RUN THE SEARCH so the list shows the new statuses rather than the
            // ones the scheduler just changed away from.
            await run();
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setBulkBusy(false);
            setPendingAction(null);
        }
    }

    return (
        <div ref={boxRef} className="relative">
            <div className="flex items-center gap-1.5">
                <div className="relative">
                    <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void run(); } }}
                        onFocus={() => { if (res) setOpen(true); }}
                        placeholder="Search container, BOL, PO, client, driver…"
                        className="h-9 w-[19rem] pl-7 pr-7"
                        aria-label="Search appointments"
                    />
                    {q && (
                        <button
                            type="button"
                            onClick={clear}
                            className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-muted"
                            aria-label="Clear search"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>
                <Button size="sm" variant="outline" onClick={() => void run()} disabled={busy}>
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
                </Button>
                {/**
                  * ⭐ CANCEL, AND IT ONLY EXISTS WHILE THERE IS SOMETHING TO CANCEL.
                  *
                  * ⛔ A PERMANENTLY VISIBLE CANCEL IS A BUTTON THAT DOES NOTHING
                  * MOST OF THE TIME, and a button that usually does nothing is one
                  * nobody trusts when it matters.
                  * ⚠️ The word is "Stop", not "Cancel": the request is already in
                  * flight and cannot be recalled. What stops is the waiting.
                  *   ** A CONTROL MUST BE NAMED FOR WHAT IT ACTUALLY DOES, NOT FOR
                  *      WHAT THE USER WISHES IT DID. **
                  */}
                {busy && (
                    <Button
                        size="sm" variant="ghost"
                        onClick={cancelSearch}
                        title="Stop waiting for this search. The request has already been sent and cannot be recalled; its answer will be discarded."
                        className="text-xs text-muted-foreground"
                    >
                        Stop
                    </Button>
                )}
            </div>

            {/**
              * ⛔ z-[200] AND AN EXPLICIT OPAQUE BACKGROUND on the panel below. Pete,
              * 2026-08-24: *"Search screen needs to sit in front of background."*
              * `bg-popover` and z-50 were not enough — the status strip and the grid's
              * hour labels showed straight through, so results were read against
              * "Requested 0 / Approved" and a 10 AM gridline.
              *
              * ⚠️ THE TOKEN WAS THE PROBLEM, NOT ONLY THE LAYER. A themed surface token
              * can be semi-transparent, and a translucent dropdown over a dense grid is
              * unreadable however high it floats. Solid colours, both themes.
              *
              * ⛔ AND THE COMMENT LIVES ABOVE THE BLOCK, NOT INSIDE IT. A JSX comment in
              * an expression position — between the `open &&` and the element it guards —
              * is a parse error. Second time this session; the first was inside a ternary
              * in UploadAppointmentsDialog.
              *
              * ⚠️ AND WRITING THAT DOWN BROKE IT AGAIN: the first draft of this very
              * comment contained a literal JSX-comment example, whose closing delimiter
              * ended this block early and corrupted everything below it. Same shape as
              * the backtick-inside-a-template-literal note in uploadRepository. Do not
              * put a comment terminator inside a comment about comments.
              */}
            {open && (
                <div className="absolute left-0 top-11 z-[200] w-[40rem] rounded-md border bg-white text-neutral-900 dark:bg-neutral-900 dark:text-neutral-100 p-2 shadow-xl">
                    {error && (
                        <div className="rounded border border-destructive/40 bg-destructive/10 p-2 text-sm">
                            {error}
                        </div>
                    )}

                    {/**
                      * ⭐ #254 — THE STATUS FILTER, above the results because it changes
                      * what they are. ⚠️ Shown even before the first search: "search on
                      * status" is a thing Pete asked for by name, and a control that
                      * appears only after a search cannot be used to shape the first one.
                      */}
                    {/**
                      * ---- ⭐⭐ THE SHARED BAR, NOT A SECOND COPY OF IT (2026-09-20) -------
                      *
                      * ⭐ Pete, 2026-09-19: *"the status bar on this search should match
                      * (size) and checkboxes of other status for consistency."*
                      *
                      * ⛔ THIS WAS A HAND-ROLLED LOOKALIKE — its own `Any` chip, its own
                      * padding, its own swatch sizes, drifting from the board's row every
                      * time that one was restyled (three times on 2026-09-19 alone).
                      *   ** A SHAPE MAINTAINED BY COPYING IS A SHAPE THAT DIVERGES. **
                      *
                      * ⛔⛔ AND IT RENDERS **NO COUNTS**, which is why `StatusFilterBar`
                      * grew a union rather than a flag. The search looks ACROSS DATES and
                      * has no day to count over; *"Requested 0"* here would be invented.
                      *   ** IDENTICAL STYLING IS CONSISTENCY. IDENTICAL CONTENT WOULD BE A
                      *      LIE ABOUT WHAT EACH VIEW CONTAINS. **
                      *
                      * ⚠️ THE ADAPTER IS THE ONLY NEW CODE. This component holds `statuses`
                      * as `string[]` because that is what `run()` takes; the shared bar
                      * speaks `Set<string>`. Converting at the boundary keeps the search's
                      * own contract untouched — Pete: *"keeping the search untouched."*
                      * ⛔ AND EVERY PATH RE-RUNS THE SEARCH, as the hand-rolled version did:
                      * a filter that changes the chips and not the results is the defect
                      * this row exists to avoid.
                      */}
                    {!error && (
                        <div className="border-b px-1 pb-1.5">
                            {/**
                              * ---- ⭐⭐ NO OVERLAY LEGEND IN THE SEARCH (Pete, 2026-09-20) --
                              *
                              * > *"Get rid of the Limits and the Dedicated on the search."*
                              *
                              * ⛔⛔ THIS IS NOT A REVERSAL OF 2026-09-19. That day Pete put
                              * `Dedicated` back on the BOARD's bar and the reasoning was
                              * explicitly about Week and Month, *"where there is no band, no
                              * handle and no badge — only an amber tint on a dedicated door"*.
                              * `showOverlayLegend` still defaults to `true` and the board is
                              * untouched.
                              *
                              * ⭐ WHAT IS DIFFERENT HERE IS THE THING BELOW THE LEGEND. Both
                              * entries are a KEY TO A COLOUR DRAWN ON A GRID — a violet limit
                              * band, an amber dedicated door. The search renders a LIST OF
                              * ROWS. Neither colour appears anywhere in this panel, so the key
                              * explains two things that are not on screen.
                              *   ** A LEGEND IS SCOPED TO THE PICTURE IT SITS BESIDE. MOVED TO
                              *      A DIFFERENT PICTURE IT DOES NOT BECOME WRONG — IT BECOMES
                              *      AN ANSWER TO A QUESTION NOBODY ASKED. **
                              *
                              * ⚠️ AND IT CAME FREE WITH THE SHARED BAR. The hand-rolled
                              * lookalike this replaced never had a legend; adopting the shared
                              * component inherited one nobody chose. That is the standing cost
                              * of sharing a component — you inherit its every decision — and
                              * the prop existed precisely so a caller could decline one.
                              */}
                            <StatusFilterBar
                                dense
                                showCounts={false}
                                showOverlayLegend={false}
                                statusFilter={new Set(statuses)}
                                toggleStatus={toggleStatus}
                                setStatusFilter={(next) => {
                                    const arr = [...next];
                                    setStatuses(arr);
                                    if (res) void run(scope, arr);
                                }}
                            />
                        </div>
                    )}

                    {/**
                      * ⭐ #262 — THE DATE, ON THE SAME ROW OF CHROME AS THE STATUS FILTER.
                      *
                      * Pete, 2026-08-26: *"would be able to change the date in the search
                      * box would be nice."*
                      *
                      * ⛔ IT SITS BESIDE THE SCOPE, NOT INSIDE THE RESULTS. The date and
                      * the Day/Week/Month scope are one question — "which range" — and
                      * splitting them across the panel is how a scheduler ends up
                      * searching a week around a day they did not mean.
                      *
                      * ⚠️ A NATIVE <input type="date">, deliberately. It gets the
                      * keyboard, the locale and the picker right on every platform we
                      * ship to, and a hand-rolled calendar in a dropdown that is already
                      * inside a dropdown is a z-index problem waiting to happen — this
                      * panel has had two.
                      *
                      * ⭐ CHANGING IT RE-RUNS IMMEDIATELY when there are results, the
                      * same as the status filter. A date that needs a second click on
                      * Search shows a list disagreeing with the box above it.
                      */}
                    {!error && (
                        <div className="flex flex-wrap items-center gap-2 border-b px-1 py-1.5 text-[11px]">
                            {/* ⭐⭐ F69 (Pete, 2026-09-28): *"Why do we have a date if it says
                                searching every date - the date above is ignored?"* → popup
                                *"Hide it on every-date searches"*. A control that does nothing
                                is worse than none, and the note apologising for it was the proof.
                                On every date: the words say so, and one link brings the date back. */}
                            {scope === 'all' ? (
                                <>
                                    <span className="text-muted-foreground">Searching every date</span>
                                    <button
                                        type="button"
                                        onClick={() => { setScope('day'); void run('day', statuses, searchDate, false); }}
                                        className="rounded px-1.5 py-0.5 underline decoration-dotted hover:bg-muted"
                                        title={`Search ${searchDate} only`}
                                    >
                                        Search one date
                                    </button>
                                </>
                            ) : (
                            <>
                            <span className="text-muted-foreground">Date</span>
                            <input
                                type="date"
                                value={searchDate}
                                onChange={e => {
                                    const next = e.target.value;
                                    // ⛔ AN EMPTY VALUE IS A CLEARED PICKER, NOT A DATE.
                                    // Every browser produces it, and passing '' to the
                                    // server would be a 400 on a control the scheduler
                                    // was only half way through using.
                                    if (!next) return;
                                    setSearchDate(next);
                                    if (res) void run(scope, statuses, next);
                                }}
                                className="rounded border bg-transparent px-1.5 py-0.5 text-[11px]"
                            />
                            {/**
                              * ⭐ ONLY WHEN IT DIFFERS FROM THE BOARD. A "today" button
                              * that is always there is noise; one that appears the moment
                              * the search and the board disagree is a signpost.
                              *
                              * ⚠️ AND IT NAMES WHAT IT GOES BACK TO. "Reset" would leave a
                              * scheduler guessing which day that is.
                              */}
                            {searchDate !== anchorDate && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSearchDate(anchorDate);
                                        if (res) void run(scope, statuses, anchorDate);
                                    }}
                                    className="rounded px-1.5 py-0.5 underline decoration-dotted hover:bg-muted"
                                >
                                    Back to {anchorDate}
                                </button>
                            )}
            {/**
                              * ---- ⛔ ~~"day around this date"~~ (Pete, 2026-09-09) -----
                              *
                              * > *"We also don't need 'day around this date'."*
                              *
                              * ⚠️ ON THE **DAY** SCOPE IT SAID THE SAME THING TWICE AND
                              * LESS WELL. The date picker is right there and the scope
                              * sentence below states the exact range — "Searched
                              * 2026-09-08." `day around this date` paraphrases both and
                              * implies a WINDOW EITHER SIDE, which the day scope does not
                              * have. So the day case is gone.
                              *
                              * ---- ⛔⛔ AND DELETING IT OUTRIGHT BROKE A GUARD ---------
                              *
                              * ⚠️ MY FIRST EDIT REMOVED IT FOR EVERY SCOPE, and
                              * `search-scope-guard` failed: *"a lone date picker implies
                              * one day when the scope may be a month."* ⭐ THE GUARD WAS
                              * RIGHT. With the calendar on Month, a bare date and no
                              * qualifier reads as "searching that day" — the picker
                              * silently understating the range by a factor of thirty.
                              *
                              * ⛔ THE FIX IS NOT TO RELAX THE GUARD TO MATCH THE CODE.
                              * Pete objected to the WORDING, not to the information, and
                              * a guard deleted to make a change pass is the change
                              * removing its own safety net.
                              *
                              * ⭐ SO: silent on `day` (his point), and on week/month it
                              * says which range the date anchors — in words that describe
                              * containment rather than "around", because a week scope is
                              * the week CONTAINING the date, not three days either side.
                              */}
                            {scope !== 'day' && (
                                <span className="ml-auto text-muted-foreground">
                                    {`the ${scope} containing this date`}
                                </span>
                            )}
                            </>
                            )}
                        </div>
                    )}

                    {/**
                      * ⛔ A REFUSAL IS RENDERED AS GUIDANCE, NOT AS AN ERROR OR AN EMPTY
                      * LIST. "Type at least 3 characters" is something the scheduler can
                      * act on; a blank panel is what makes people stop trusting a search.
                      */}
                    {!error && res?.refused && (
                        <div className="p-2 text-sm text-muted-foreground">{res.reason}</div>
                    )}

                    {/**
                      * ⭐ WHAT CAN I SEARCH ON — answered on the screen, not in a manual.
                      * Pete asked exactly this the first time he used it, which means
                      * every scheduler will. ⚠️ Shown only before the first search, so it
                      * teaches once and then gets out of the way.
                      */}
                    {!error && !res && (
                        <div className="p-2 text-xs text-muted-foreground">
                            <div className="mb-1 font-medium text-foreground">You can search on</div>
                            Container · BOL · PO · ASN · Reference · Trailer number · Trailer plate ·
                            Appointment # · Document # · Client name or code · Carrier name or code ·
                            Driver name · Comments
                            <div className="mt-1.5">
                                Punctuation is ignored on the numbers, so PWN-5900139 finds PWN5900139.
                                Three characters minimum. Searches <strong>all dates</strong> and every facility you can see; narrow it below if there is too much.
                            </div>
                        </div>
                    )}

                    {!error && res && !res.refused && (
                        <>
                            {/**
                              * ⛔⛔ THE SCOPE SENTENCE IS SHOWN ON EVERY RESULT, NOT ONLY
                              * ON AN EMPTY ONE.
                              *
                              * ⚠️ An empty result inside a range and an empty result
                              * because nothing exists look IDENTICAL, and a scheduler
                              * chasing a claim from last year reads the first as the
                              * second. ⭐ A SHORT list is the same lie in miniature —
                              * three results look like "there are three" — so the range
                              * is stated whether the list is empty, short or full.
                              */}
                            <div className="flex items-center justify-between gap-2 border-b px-2 py-1.5 text-xs text-muted-foreground">
                                <span>{res.scopeSentence}</span>
                                <div className="flex shrink-0 items-center gap-2">
                                    {/**
                                      * ⭐ ONE RUNG AT A TIME, and the server says which one
                                      * is next. A screen that computed "wider" itself could
                                      * offer something NARROWER after a scope change, which
                                      * looks like the search losing results as you widen it.
                                      */}
                                    {res.nextScope && (
                                        <button
                                            type="button"
                                            className="inline-flex items-center gap-1 underline underline-offset-2 hover:text-foreground"
                                            onClick={() => { setScope(res.nextScope!); void run(res.nextScope!, statuses, searchDate, false); }}
                                        >
                                            <CalendarDays className="h-3 w-3" />
                                            Widen to {res.nextScopeLabel}
                                        </button>
                                    )}
                                    {res.scope !== 'all' && (
                                        <button
                                            type="button"
                                            className="underline underline-offset-2 hover:text-foreground"
                                            onClick={() => { setScope('all'); void run('all', statuses, searchDate, false); }}
                                        >
                                            All dates
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/**
                              * ⭐⭐ "AND HOW MANY ARE THERE ELSEWHERE?" — the counterweight
                              * to a one-day default (#255).
                              *
                              * ⚠️ `outsideScope` IS null WHEN THE QUESTION WAS NOT ASKED,
                              * which is NOT the same as zero. Rendering null as "none
                              * elsewhere" would be the screen inventing an answer nobody
                              * looked for.
                              */}
                            {typeof res.outsideScope === 'number' && res.outsideScope > 0 && (
                                <div className="mt-1 flex items-center justify-between gap-2 rounded bg-blue-500/10 px-2 py-1 text-xs text-blue-800 dark:text-blue-300">
                                    <span>
                                        {res.outsideScope > 50 ? '50+' : res.outsideScope} more match
                                        {res.outsideScope === 1 ? 'es' : ''} on other dates.
                                    </span>
                                    <button
                                        type="button"
                                        className="shrink-0 underline underline-offset-2"
                                        onClick={() => { setScope('all'); void run('all', statuses, searchDate, false); }}
                                    >
                                        Show them
                                    </button>
                                </div>
                            )}

                            {/**
                              * ---- ⭐⭐⭐ THE SCREEN SAYS IT WIDENED, AND OFFERS IT BACK --
                              *
                              * ⛔⛔ THIS BANNER IS THE OTHER HALF OF THE FEATURE, NOT ITS
                              * DECORATION. Without it the search answers a question the
                              * scheduler did not ask and looks exactly like one that did —
                              * and every row below is then read as belonging to a day it
                              * does not belong to.
                              *
                              * ⛔ BLUE, NOT AMBER, AND THAT IS A JUDGEMENT ABOUT WHAT
                              * HAPPENED. The amber banner below reports something LOST
                              * (rows cut by the cap); this reports something GAINED. It
                              * matches the blue *"more matches on other dates"* strip
                              * directly above, because it is the same fact — acted on
                              * rather than offered.
                              *
                              * ⚠️ CANCEL RE-RUNS AT `from` WITH AUTO-WIDEN OFF. Without
                              * that argument the narrow search finds nothing, widens
                              * itself again, and Cancel becomes a button that does
                              * nothing twice.
                              *   ** AN UNDO THAT RE-ENTERS THE THING IT IS UNDOING IS A
                              *      LOOP WITH A FRIENDLY LABEL. **
                              */}
                            {autoWiden && (
                                <div className="mt-1 flex items-center justify-between gap-2 rounded bg-blue-500/10 px-2 py-1 text-xs text-blue-800 dark:text-blue-300">
                                    <span>
                                        Nothing matched the range you were on — widened to {autoWiden.toLabel}.
                                    </span>
                                    <button
                                        type="button"
                                        className="shrink-0 underline underline-offset-2"
                                        onClick={() => {
                                            const back = autoWiden.from;
                                            setAutoWiden(null);
                                            setScope(back);
                                            void run(back, statuses, searchDate, false);
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}

                            {/**
                              * ⚠️ THE CAP IS NAMED. A silently truncated list reads as
                              * "this is everything", and the row that was cut is the one
                              * the scheduler is looking for often enough to matter.
                              */}
                            {res.capped && (
                                <div className="mt-1 rounded bg-amber-500/10 px-2 py-1 text-xs text-amber-700 dark:text-amber-400">
                                    Showing the first 50 matches — narrow the search to see the rest.
                                </div>
                            )}

                            {/**
                              * ⭐ #254 — WHAT THE LAST BULK CHANGE ACTUALLY DID.
                              * ⛔ THE SKIPPED ROWS ARE NAMED WITH THEIR REASONS. "37 of 40
                              * changed" without saying which three and why is a batch that
                              * half-applied and called itself done.
                              */}
                            {/* ⬜ Suppressed with the actions that produce it — BULK_IN_SEARCH. */}
                            {BULK_IN_SEARCH && bulkReport && (
                                <div className="mt-1 rounded border border-emerald-500/40 bg-emerald-500/10 px-2 py-1.5 text-xs">
                                    <div className="font-medium text-emerald-800 dark:text-emerald-300">
                                        {bulkReport.changed} changed
                                        {bulkReport.skipped.length > 0 ? `, ${bulkReport.skipped.length} left alone` : ''}.
                                        {' '}Carriers were not notified.
                                    </div>
                                    {bulkReport.skipped.length > 0 && (
                                        <ul className="mt-1 max-h-24 space-y-0.5 overflow-y-auto text-muted-foreground">
                                            {bulkReport.skipped.map(s => (
                                                <li key={s.appointmentId}>#{s.appointmentId} — {s.reason}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            )}

                            {/**
                              * ⛔ ABOVE THE RESULTS, AND VISUALLY A DIFFERENT KIND OF THING.
                              * These are not hits — they are an offer to stop looking at
                              * hits and scope the whole board instead. A row styled like the
                              * results would be read as a result, and clicking it does
                              * something much larger than jumping to a load.
                              * ⚠️ Closes the search, because the thing it changed is behind
                              * the search box.
                              */}
                            {onScopeParty && pinnedParties.length > 0 && (
                                <div className="border-b bg-muted/40 px-1 py-1">
                                    {pinnedParties.map((p) => (
                                        <button
                                            key={`${p.kind}:${p.code}`}
                                            type="button"
                                            onClick={() => { onScopeParty(p.kind, p.code, p.name); setOpen(false); }}
                                            className="flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-[12px] hover:bg-muted"
                                        >
                                            <span className="font-medium">All {p.name} appointments</span>
                                            <span className="text-[10.5px] text-muted-foreground">
                                                ({p.kind})
                                            </span>
                                            {/* ⚠️ THE COUNT IS OF THE HITS ON SCREEN, NOT OF THE
                                                PARTY'S DAY — and it says so, because those are
                                                different numbers and the board will show the
                                                second one. */}
                                            <span className="ml-auto text-[10.5px] text-muted-foreground">
                                                {p.n} here
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/**
                              * ⛔⛤ MOVED 2026-09-22, BEFORE IT EVER SHIPPED. This block was
                              * first written INSIDE the non-empty branch of the ternary
                              * below -- so it would have rendered only when the scoped
                              * search already found something.
                              *
                              * ⚠️ `A00000364` returns ZERO scoped results. The exact hit is
                              * the entire answer, and it would have been invisible in the
                              * one case the feature exists for.
                              *   ** A SECTION PLACED INSIDE THE "WE FOUND SOMETHING" BRANCH
                              *      CANNOT ANSWER THE "WE FOUND NOTHING" CASE. **
                              * ⭐ It sits ABOVE the ternary now and renders in both.
                              */}
                                {/**
                                  * ---- ⭐⭐⭐ FOUND ELSEWHERE (decision 210) ----------
                                  *
                                  * ⛔ Pete: `A00000364` read "Nothing matched" on All
                                  * dates. It exists, at facility 262, while he was on
                                  * Denver.
                                  *   ** A SEARCH BOUND TO ONE FACILITY CANNOT SAY
                                  *      "NOTHING MATCHED". IT CAN ONLY SAY "NOTHING
                                  *      HERE". **
                                  *
                                  * ⚠️ ABOVE THE SCOPED LIST, because it is the answer to
                                  * what was MEANT when the scoped list is the answer to
                                  * what was ASKED -- and a scheduler who typed a whole
                                  * appointment number meant that one.
                                  * ⛔ NO CHECKBOXES. These rows are not in `picked` and
                                  * must not look as though they are.
                                  */}
                                {exactHits.length > 0 && (
                                    <div className="mt-2 rounded border border-amber-300 bg-amber-50/60">
                                        <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-amber-800">
                                            Exact match{exactHits.length === 1 ? '' : 'es'} found elsewhere
                                        </div>
                                        <ul className="divide-y divide-amber-200">
                                            {exactHits.map((h) => (
                                                <li key={`exact-${h.appointmentId}`}>
                                                    <button
                                                        type="button"
                                                        className="w-full px-2 py-1.5 text-left text-xs hover:bg-amber-100"
                                                        /* ⛔ THE FACILITY IS PASSED, so the jump
                                                           moves the picker too. Landing on the
                                                           right DATE at the wrong SITE is an
                                                           empty board that looks like a lie. */
                                                        onClick={() => { setOpen(false); onJump(h.localDate, h.appointmentId, h.facilityCode); }}
                                                    >
                                                        <span className="font-medium">{h.appointmentNumber ?? h.appointmentId}</span>
                                                        {/* ⚠️ THE FACILITY AND THE DAY, ALWAYS. A row
                                                            that does not say where it is reads as
                                                            local, and booking another site's freight
                                                            is the failure this whole feature risks. */}
                                                        <span className="ml-2 rounded bg-amber-200 px-1 py-0.5 text-[10px] font-semibold text-amber-900">
                                                            {h.facilityCode}
                                                        </span>
                                                        <span className="ml-2 text-muted-foreground">{h.localDate}</span>
                                                        {h.statusCode && (
                                                            <span className="ml-2 text-muted-foreground">· {h.statusCode}</span>
                                                        )}
                                                        {h.matchedOn && (
                                                            <span className="ml-2 text-muted-foreground">· {h.matchedOn}: {h.matchedValue}</span>
                                                        )}
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                            {hits.length === 0 ? (
                                /**
                                 * ---- ⛔⛔ "NOTHING MATCHED" IS A CLAIM ABOUT THE WORLD ---
                                 *
                                 * > Pete, 2026-09-09: *"Nothing found."* — said while the
                                 * > WMS half of this very search was timing out.
                                 *
                                 * ⚠️ THIS PANEL RUNS **TWO** SEARCHES: the dock's own
                                 * appointments, and the WMS cache. When the WMS half fails
                                 * this line used to report absence anyway — an unqualified
                                 * "Nothing matched" for a search that never ran.
                                 *
                                 * ⛔ AND THAT IS THE SENTENCE THAT TURNS A DRIVER AWAY FROM
                                 * A GATE. A scheduler reads "nothing matched" as "this load
                                 * does not exist", not as "one of the two places I look was
                                 * unreachable". The WMS failure was rendered further down
                                 * the panel, below the fold on a short window, in grey.
                                 *
                                 * ⭐ SO THE EMPTY STATE ASKS THE OTHER HALF FIRST. It states
                                 * what was NOT searched before it says what was not found,
                                 * and it never uses the word "nothing" when half the search
                                 * is missing.
                                 */
                                <div className="p-3 text-sm">
                                    {res.wms && !res.wms.available ? (
                                        <div className="flex items-start gap-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-amber-900">
                                            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                            <span>
                                                <span className="font-medium">This search is incomplete.</span>{' '}
                                                No booking matched “{q}”, and the WMS could not be
                                                searched, so an order that exists only in Camelot or
                                                Deposco would not appear here.{' '}
                                                <span className="font-medium">Do not treat this as “not found”.</span>{' '}
                                                <button type="button" className="underline underline-offset-2"
                                                        onClick={() => { void run(scope); }}>
                                                    Try again
                                                </button>
                                            </span>
                                        </div>
                                    ) : (
                                        /**
                                          * ---- ⛔⛔ IT SAID "NOTHING" WITH A MATCH ON SCREEN --
                                          *
                                          * > Pete, 2026-09-27, on a search for `so1946015` that
                                          * > printed *"Nothing matched"* above a WMS section
                                          * > holding that exact order: *"it matched an
                                          * > order/appointment there so not sure why it says
                                          * > nothing matched"*.
                                          *
                                          * ⭐ HE WAS READING IT EXACTLY AS WRITTEN. This sentence
                                          * is about APPOINTMENTS — `hits` — and it was rendered
                                          * directly above `<WmsSection>`, which had found the
                                          * order at facility 261. The screen said nothing, then
                                          * showed something, eight pixels apart.
                                          *
                                          * ⛔⛔ AND THIS FILE ALREADY HELD THE RULE IT WAS
                                          * BREAKING, written for the neighbouring case:
                                          *   ** A SEARCH BOUND TO ONE FACILITY CANNOT SAY
                                          *      "NOTHING MATCHED". IT CAN ONLY SAY "NOTHING
                                          *      HERE". **
                                          * The empty state was taught to qualify itself when the
                                          * WMS half FAILED (the amber banner above) and never
                                          * when the WMS half SUCCEEDED. ⚠️ The failure case is
                                          * the rarer one; the success case is every search for
                                          * an order that has no booking yet, which is precisely
                                          * what a scheduler types when they are about to make
                                          * one.
                                          *   ** A CAVEAT WRITTEN FOR THE ERROR PATH IS NOT A
                                          *      CAVEAT. THE SENTENCE HAS TO BE TRUE ON THE PATH
                                          *      PEOPLE ACTUALLY WALK. **
                                          *
                                          * ⭐ SO IT NAMES WHAT IT SEARCHED — "No appointment
                                          * matched" — and, when there are orders below, points
                                          * at them instead of ending in a full stop.
                                          */
                                        <span className="text-muted-foreground">
                                            No appointment matched “{q}”.
                                            {/* ⚠️ ONE LINE, DELIBERATELY. A template literal broken
                                                across source lines carries its newline and its
                                                indentation into the string — HTML collapses it, so
                                                it looks fine and is wrong, which is the worst pair. */}
                                            {wmsFound > 0
                                                ? ` The WMS has ${wmsFound === 1 ? 'an order' : `${wmsFound} orders`} for it — see below.`
                                                : res.scope !== 'all'
                                                    ? ' It may fall outside the range above.'
                                                    : ''}
                                        </span>
                                    )}
                                </div>
                            ) : (
                                <>
                                    {/**
                                      * ⛔⛔ SELECT-ALL MEANS THE ROWS ON SCREEN, AND THE
                                      * LABEL SAYS SO WITH A NUMBER IN IT — ⛔⚤ ~~ALWAYS~~,
                                      * **ONLY WHEN THE LIST IS CAPPED** (Pete, 2026-09-20).
                                      *
                                      * > *"Should say 'Select All' not Select all 1 shown"*
                                      *
                                      * ⚠️ WHEN THE LIST IS CAPPED THE NUMBER IS THE WHOLE
                                      * DIFFERENCE BETWEEN A TOOL AND AN ACCIDENT. "Select
                                      * all" over a filter that matched 400 rows, applied
                                      * to a list showing 50, is how a day's schedule gets
                                      * cancelled by somebody who believed they could see
                                      * what they were selecting. ⛔ THAT ARGUMENT IS INTACT
                                      * AND THE CAPPED CASE STILL CARRIES ITS COUNT.
                                      *
                                      * ⭐⭐ WHAT PETE CAUGHT IS THAT IT WAS BEING PAID IN
                                      * EVERY OTHER CASE. `Select all 1 shown` warns about a
                                      * gap between the list and the match set at the one
                                      * moment there provably is none — and eleven words of
                                      * caution over a single visible row teaches a scheduler
                                      * that this label is noise, which is exactly the habit
                                      * the capped case needs her not to have.
                                      *   ** A WARNING SHOWN WHEN THERE IS NOTHING TO WARN
                                      *      ABOUT SPENDS THE ATTENTION THE REAL ONE NEEDS. **
                                      *
                                      * ⛔ `res.capped`, NOT `hits.length > 1`. The condition
                                      * has to be *"is anything hidden"*, which is a fact the
                                      * server reports — not *"are there several rows"*, which
                                      * only correlates with it. ⚠️ The amber banner above
                                      * says the same thing in a sentence; this is the short
                                      * form ON the control, where the click happens.
                                      */}
                                    {BULK_IN_SEARCH && (
                                    <div className="mt-1 flex items-center justify-between gap-2 border-b px-2 pb-1.5">
                                        <button
                                            type="button"
                                            onClick={toggleAll}
                                            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                                        >
                                            <span className={`inline-flex h-3.5 w-3.5 items-center justify-center rounded-sm border ${
                                                allOnScreenPicked ? 'bg-foreground border-transparent' : 'border-input bg-background'
                                            }`}>
                                                {allOnScreenPicked && <Check className="h-2.5 w-2.5 text-background" strokeWidth={3} />}
                                            </span>
                                            Select All{res.capped ? ` (${hits.length} shown)` : ''}
                                        </button>
                                        {picked.size > 0 && (
                                            <span className="text-xs text-muted-foreground">{picked.size} selected</span>
                                        )}
                                    </div>
                                    )}

                                    <ul className="mt-1 max-h-80 divide-y overflow-y-auto">
                                        {hits.map((h) => (
                                            <SearchRow
                                                key={h.appointmentId}
                                                hit={h}
                                                /* ⬜ Suppressed with the rest — see BULK_IN_SEARCH.
                                                   ⛔ The row still RECEIVES these so restoring the
                                                   feature is one constant, not a prop re-thread. */
                                                selectable={BULK_IN_SEARCH}
                                                checked={picked.has(h.appointmentId)}
                                                onToggle={() => togglePick(h.appointmentId)}
                                                onPick={() => { setOpen(false); onJump(h.localDate, h.appointmentId); }}
                                            />
                                        ))}
                                    </ul>

                                    {/**
                                      * ⭐ THE ACTION BAR APPEARS ONLY WITH A SELECTION, and
                                      * it lives at the BOTTOM — under the list it acts on,
                                      * where the eye ends up after reading it.
                                      *
                                      * ⛔ THE BUTTONS ARE SPACED AND CANCEL IS SEPARATED.
                                      * See the note at the top of this file: adjacency
                                      * between Approve and Cancel is the mis-click that
                                      * matters.
                                      */}
                                    {BULK_IN_SEARCH && picked.size > 0 && (
                                        <div className="mt-1.5 flex flex-wrap items-center gap-2 rounded border bg-muted/40 px-2 py-1.5">
                                            <span className="text-xs font-medium">
                                                {picked.size} selected
                                            </span>
                                            <div className="flex-1" />
                                            {BULK_ACTIONS.filter(a => !a.destructive).map(a => (
                                                <Button
                                                    key={a.action}
                                                    size="sm"
                                                    variant="outline"
                                                    disabled={bulkBusy}
                                                    onClick={() => setPendingAction(a.action)}
                                                >
                                                    {a.label}
                                                </Button>
                                            ))}
                                            <span className="mx-1 h-5 w-px bg-border" aria-hidden />
                                            {BULK_ACTIONS.filter(a => a.destructive).map(a => (
                                                <Button
                                                    key={a.action}
                                                    size="sm"
                                                    variant="destructive"
                                                    disabled={bulkBusy}
                                                    onClick={() => setPendingAction(a.action)}
                                                >
                                                    {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : a.label}
                                                </Button>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                            {/**
                              * ---- ⭐⭐ #256 — AND WHAT THE WMS KNOWS ----------------
                              *
                              * Pete, 2026-08-26: *"it should probably search the WMS
                              * data too but somehow identify it as In Camelot or In
                              * Deposco"*.
                              *
                              * ⛔ ITS OWN SECTION, BELOW THE BOOKINGS, WITH ITS OWN
                              * HEADING. Never merged into the list above: a WMS order is
                              * not a booking, has no door and no driver, and cannot be
                              * checked in against. Blending them reads as "here are your
                              * results" and invites exactly that mistake.
                              */}
                            <WmsSection wms={res.wms} />
                        </>
                    )}
                </div>
            )}

            {/**
              * ⛔ ConfirmDialog, NOT window.confirm. Pete on native dialogs: *"this is
              * lazy coding and we never do this."*
              *
              * ⚠️ THE MESSAGE CARRIES THE COUNT, WHAT THE ACTION MEANS, AND THE FACT
              * THAT NOBODY IS NOTIFIED. A scheduler who cancels forty loads and assumes
              * the carriers were told is how forty trucks arrive anyway.
              */}
            <ConfirmDialog
                /* ⬜ BULK_IN_SEARCH — belt as well as braces. Nothing can set
                   `pendingAction` while the buttons are suppressed, but a confirm that
                   could open with no way to have asked for it is the kind of thing a
                   later change re-enables by accident. */
                open={BULK_IN_SEARCH && pendingAction !== null}
                title={action ? `${action.label} ${picked.size} appointment${picked.size === 1 ? '' : 's'}?` : ''}
                message={action
                    ? `${action.describe(picked.size)}\n\nCarriers and drivers are NOT notified — that is a separate action, exactly as it is when you change one appointment.`
                    : ''}
                confirmLabel={action?.label ?? 'Confirm'}
                destructive={action?.destructive ?? false}
                requireReason={action?.action === 'CANCEL'}
                reasonLabel="Reason (recorded against every appointment)"
                reasonPlaceholder="Why are these being cancelled?"
                busy={bulkBusy}
                onConfirm={(reason) => void applyBulk(reason)}
                onCancel={() => setPendingAction(null)}
            />
        </div>
    );
}

/** ⭐ One row. The identifier that MATCHED is shown, because that is why it is here. */
function SearchRow({ hit, checked, onToggle, onPick, selectable = true }: {
    hit: AppointmentSearchHit;
    checked: boolean;
    onToggle: () => void;
    onPick: () => void;
    /** ⬜ False suppresses the checkbox — see `BULK_IN_SEARCH`. Defaults true so the
     *  row is unchanged for any caller that has not opted out. */
    selectable?: boolean;
}) {
    /**
     * ⭐ THE SERVER SAYS WHICH FIELD MATCHED *AND* WHAT IT HELD.
     *
     * ⛔ THIS USED TO BE A TERNARY mapping label→field, and it covered SEVEN of the
     * thirteen labels. Searching a client name fell through to a container number —
     * or to a dash — presented as the thing that matched. ⚠️ Exactly the "two lists
     * that must agree forever" fault the label is computed server-side to avoid; I
     * reintroduced it one layer up.
     *
     * ⚠️ The fallback is for a row the matcher could not explain — it still shows,
     * because a label we cannot produce must never suppress a result the database
     * found.
     */
    const idValue = hit.matchedValue
        ?? hit.containerNumber ?? hit.referenceNumber ?? hit.poNumber ?? hit.bolNumber;

    /**
     * ---- ⭐⭐⭐ THE LOAD'S OWN NUMBERS, ON EVERY ROW (Pete, 2026-09-20) ----------
     *
     * > *"The rows need the appointment number, PO, reference, container and the
     * > arrival time — blank when there isn't one."*
     *
     * ⭐⭐ NOT ONE FIELD WAS ADDED TO ANY DTO. `AppointmentSearchHit` has carried
     * `appointmentNumber`, `poNumber`, `referenceNumber`, `containerNumber` and
     * `scheduledStartUtc` since #145 — the row simply never rendered them. ⚠️ Worth
     * recording, because "the search needs more fields" reads as a backend task and
     * would have gone on a list behind a deploy it does not need.
     *   ** BEFORE ASKING FOR A FIELD, READ THE DTO. THE GAP IS AS OFTEN IN THE
     *      RENDER AS IN THE QUERY. **
     *
     * ⛔ BLANK MEANS ABSENT, NOT A DASH. A row of `PO — · Ref — · Container —`
     * spends four times the width to say nothing, and a scheduler scanning fifty
     * rows for the one with a container learns to ignore the whole line.
     *
     * ⛔⛔ AND THE MATCHED FIELD IS LEFT OUT, DELIBERATELY. Line 2 already prints it
     * with the word that explains WHY this row is here ("Container PWN5900139");
     * repeating it below turns the one line that answers a question into wallpaper
     * beside three that do not.
     *   ** TWO LINES THAT CARRY THE SAME VALUE ARE NOT REDUNDANT IF THEY ANSWER
     *      DIFFERENT QUESTIONS — BUT THESE ANSWER THE SAME ONE. **
     */
    const numbers: [string, string | null][] = [
        ['Appt', hit.appointmentNumber],
        ['PO', hit.poNumber],
        ['Ref', hit.referenceNumber],
        ['Container', hit.containerNumber],
    ];
    const extraNumbers = numbers.filter(
        ([label, v]) => !!v?.trim() && !(hit.matchedOn === label && v === hit.matchedValue),
    );

    /**
     * ⚠️ THE BROWSER'S ZONE, LIKE EVERY OTHER TIME ON THIS SCREEN. `localDate` is
     * computed facility-side and `scheduledStartUtc` is not — so a scheduler whose
     * workstation sits in a different zone from the dock reads a shifted clock here,
     * exactly as they do on the board, the check-in rail and the paperwork tray.
     * ⛔ NOT FIXED HERE. One row formatting time differently from the nine places
     * around it is worse than nine places being consistently wrong, and the fix is a
     * facility offset reaching all of them.  ⬜ Recorded, not patched locally.
     */
    const arrivalTime = hit.scheduledStartUtc
        ? new Date(hit.scheduledStartUtc).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
        : null;

    return (
        <li className="flex items-start gap-1">
            {/**
              * ⛔ THE CHECKBOX IS ITS OWN BUTTON, OUTSIDE THE JUMP BUTTON. Nesting an
              * interactive element inside another is invalid HTML and — more to the
              * point here — a click on the checkbox would ALSO fire the jump, closing
              * the panel and navigating away the instant somebody tried to select a row.
              */}
            {selectable && (
            <button
                type="button"
                onClick={onToggle}
                aria-label={checked ? 'Deselect' : 'Select'}
                className="mt-2.5 shrink-0 pl-1"
            >
                <span className={`inline-flex h-3.5 w-3.5 items-center justify-center rounded-sm border ${
                    checked ? 'bg-foreground border-transparent' : 'border-input bg-background'
                }`}>
                    {checked && <Check className="h-2.5 w-2.5 text-background" strokeWidth={3} />}
                </span>
            </button>
            )}
            <button
                type="button"
                onClick={onPick}
                className="flex min-w-0 flex-1 items-start justify-between gap-3 px-2 py-2 text-left hover:bg-muted"
            >
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="truncate font-medium">
                            {hit.clientName || hit.clientCode || '—'}
                        </span>
                        {hit.statusCode && (
                            <span className="shrink-0 rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                                {statusLabel(hit.statusCode)}
                            </span>
                        )}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                        {hit.matchedOn && idValue
                            ? <><span className="text-foreground/70">{hit.matchedOn}</span> {idValue}</>
                            : idValue ?? '—'}
                        {hit.carrierName ? ` · ${hit.carrierName}` : ''}
                        {hit.driverName && hit.matchedOn !== 'Driver' ? ` · ${hit.driverName}` : ''}
                    </div>
                    {/* ⚠️ `truncate`, NOT `flex-wrap`. Four numbers that wrap make rows
                        of different heights, and a list you scan by position stops being
                        scannable the moment the rows stop lining up. The full value is in
                        the title attribute for the one row that needs it. */}
                    {extraNumbers.length > 0 && (
                        <div
                            className="truncate text-[11px] text-muted-foreground/80"
                            title={extraNumbers.map(([l, v]) => `${l} ${v}`).join(' · ')}
                        >
                            {extraNumbers.map(([label, v], i) => (
                                <span key={label}>
                                    {i > 0 ? ' · ' : ''}
                                    <span className="text-foreground/50">{label}</span> {v}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
                <div className="shrink-0 text-right text-xs">
                    <div className="font-medium">
                        {hit.localDate}
                        {/* ⭐ THE TIME SITS WITH THE DATE, not on the identifier line. They
                            are one fact — WHEN — and splitting them across the row is how a
                            scheduler reads the date of one appointment beside the hour of another. */}
                        {arrivalTime && <span className="ml-1.5 font-normal text-muted-foreground">{arrivalTime}</span>}
                    </div>
                    <div className="text-muted-foreground">
                        {/* ⚠️ NO "Door" PREFIX. The door's own Name is already
                            "Dock Door 122", so prefixing produced "Door Dock Door 122". */}
                        {hit.doorName ?? 'No door'}
                        {hit.palletCount !== null ? ` · ${hit.palletCount} plt` : ''}
                    </div>
                </div>
            </button>
        </li>
    );
}

/**
 * ⛔ "NOT IN THE WMS" AND "COULD NOT READ THE WMS" ARE DIFFERENT SENTENCES.
 *
 * ⚠️ An empty section rendered for a failed lookup tells a scheduler the order does
 * not exist anywhere — which is the strongest possible version of the wrong answer,
 * and the reason this whole search feature is shaped the way it is. So a failure
 * says so, in place, and the appointment results above stand on their own.
 */
function WmsSection({ wms }: { wms?: { available: boolean; reason?: string; results: WmsSearchHit[]; capped: boolean } }) {
    if (!wms) return null;

    if (!wms.available) {
        return (
            <div className="mt-2 rounded border border-amber-400/50 bg-amber-500/10 px-2 py-1.5 text-[11px] text-amber-800 dark:text-amber-300">
                {wms.reason ?? 'The WMS could not be searched just now.'} The bookings above are unaffected.
            </div>
        );
    }

    if (wms.results.length === 0) return null;

    return (
        <div className="mt-2 border-t pt-1.5">
            <div className="flex items-center justify-between px-2 pb-1 text-[11px] text-muted-foreground">
                <span className="font-medium text-foreground/80">Also in the WMS</span>
                {/**
                  * ⚠️ SAID PLAINLY, ONCE. Every row below is an order, not a booking;
                  * repeating it per row would be noise, and omitting it entirely is how
                  * the distinction gets lost.
                  */}
                <span>These are orders, not bookings — no door, no driver.</span>
            </div>
            {wms.capped && (
                <div className="mx-2 mb-1 rounded bg-amber-500/10 px-2 py-1 text-[11px] text-amber-700 dark:text-amber-400">
                    Showing the first 20 — narrow the search to see the rest.
                </div>
            )}
            <ul className="max-h-56 divide-y overflow-y-auto">
                {wms.results.map(w => <WmsRow key={`${w.wmsSource}-${w.orderType}-${w.orderNumber}`} hit={w} />)}
            </ul>
        </div>
    );
}

/** ⚠️ A cache row's age, in words. "3 days ago" is checkable; a timestamp is not. */
/* ⛔⤴ ~~`function staleness(...)`~~ — MOVED TO `lib/utils.ts` 2026-09-27, unchanged,
   when the order detail needed the same sentence. ⭐ The two-hour floor, the future-
   timestamp guard and the h/d switch are all preserved there with their reasoning;
   this file now imports it.
     ** THE SECOND CALLER IS THE MOMENT. A HELPER COPIED ONCE IS COPIED AGAIN. ** */

function WmsRow({ hit }: { hit: WmsSearchHit }) {
    const when = hit.plannedArrivalDate ?? hit.plannedShipDate;
    const age = staleness(hit.rowUpdatedAt);
    return (
        <li className="px-2 py-2">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5">
                        {/**
                          * ⭐⭐ THE BADGE IS THE FEATURE. "In Deposco" is what turns a
                          * dead end into a next step — it tells the scheduler which
                          * system to go and look in, and which team to ring.
                          */}
                        <span className="shrink-0 rounded bg-indigo-500/15 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-indigo-700 dark:text-indigo-300">
                            In {hit.wmsSource ?? 'the WMS'}
                        </span>
                        {/**
                          * ⭐⭐ ANOTHER SITE'S SHIPMENT, SAID OUT LOUD (2026-08-27).
                          *
                          * Pete: *"for client search of the shipment in camelot, lets show
                          * them all no matter facility because the client or us may have
                          * put it in wrong."*
                          *
                          * ⛔ THE WIDENING IS THE FEATURE AND THE LABEL IS WHAT MAKES IT
                          * SAFE. A shipment keyed to the wrong warehouse is exactly what a
                          * scheduler hunts for by number — but the row for Brighton's
                          * freight is otherwise IDENTICAL to the row for theirs, and the
                          * only thing separating them is this badge.
                          *
                          * ⚠️ AMBER, NOT RED. It is not an error — half the time it is the
                          * answer being looked for. It is a fact that changes what to do
                          * next: fix the WMS, or ring the other site.
                          */}
                        {hit.elsewhere && hit.facility && (
                            <span
                                className="shrink-0 rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-semibold
                                           uppercase tracking-wide text-amber-800 dark:text-amber-300"
                                title="The WMS has this shipment under a different facility — it may be keyed wrong"
                            >
                                at {hit.facility}
                            </span>
                        )}
                        <span className="truncate font-medium">{hit.customer || hit.businessUnit || '—'}</span>
                        {hit.status && (
                            <span className="shrink-0 rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                                {hit.status}
                            </span>
                        )}
                        {/**
                          * ⭐ ALREADY BOOKED IS THE SECOND MOST USEFUL FACT ON THE ROW,
                          * and it is NOT date-scoped — "booked here at all, ever". A load
                          * booked for next month must not read as unbooked.
                          */}
                        {/**
                          * ⚠️ "here" MEANS THE FACILITY ON SCREEN, AND THAT STAYS TRUE FOR
                          * A CROSS-SITE ROW — the booked check is still asked of THIS site,
                          * which is the useful question: *should I book it?*
                          *
                          * ⛔ SO A ROW FROM ANOTHER SITE READS "at BRIGHTON · not booked
                          * here", which is exactly the situation and not a contradiction.
                          * Suppressing the badge on those rows would leave the scheduler
                          * unable to tell an unbooked local load from a foreign one.
                          */}
                        {hit.alreadyBooked ? (
                            <span className="shrink-0 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-300">
                                already booked here
                            </span>
                        ) : (
                            <span className="shrink-0 rounded bg-slate-500/15 px-1.5 py-0.5 text-[10px] font-medium text-slate-700 dark:text-slate-300">
                                not booked here
                            </span>
                        )}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                        {hit.matchedWmsColumn && hit.matchedWmsValue
                            ? <><span className="text-foreground/70">{hit.matchedWmsColumn}</span> {hit.matchedWmsValue}</>
                            : hit.orderNumber}
                        {hit.orderType ? ` · ${hit.orderType}` : ''}
                        {hit.containerNumber ? ` · ${hit.containerNumber}` : ''}
                    </div>
                </div>
                <div className="shrink-0 text-right text-xs">
                    <div className="font-medium">{when ? when.slice(0, 10) : 'no date'}</div>
                    {/* ⚠️ Only shown when it is old enough to matter — see staleness(). */}
                    {age && <div className="text-muted-foreground">{age}</div>}
                </div>
            </div>
        </li>
    );
}
